import {
    Children,
    forwardRef,
    isValidElement,
    ReactNode,
    useMemo,
} from 'react';
import {
    DesignSystemChipRadius,
    DesignSystemChipSize,
    DesignSystemColorScheme,
} from '@upheal/design-system/theme/design-system/types';
import { Theme, useTheme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { chipRadiusValues, chipSizeValues, getSizeStyles } from './styleUtils';
import { getRadiusStyles } from '@upheal/design-system/utils/radius';
import { Slots, AvailableSlots } from '@upheal/design-system/utils/Slots';
import { SlotProvider } from '@upheal/design-system/utils/SlotContext';
import { useSlotContext } from '@upheal/design-system/utils/useSlotContext';
import { Stack } from '@mui/material';
import { Typography } from '@upheal/design-system/components/Typography/Typography';

interface ChipBaseProps {
    /**
     * The color scheme of the chip, affecting background, text, and border colors.
     * @default 'accent1'
     */
    colorScheme?: DesignSystemColorScheme;
    /**
     * The size of the chip, affecting padding, height, and font size
     * @default 'md'
     */
    size?: DesignSystemChipSize;
    /**
     * The border radius of the chip.
     * @default 'rounded'
     */
    radius?: DesignSystemChipRadius;

    /**
     * Slot for component when used within other components like buttons.
     * Use 'trailingItem' when placing the chip at the end of a button to ensure proper padding.
     */
    slot?: Exclude<AvailableSlots, 'card'>;

    sx?: SxProps<Theme>;
}

// TODO: Add Avatar (mutually exclusive with iconOnly and leadingIcon) + Badge
type LayoutProps = {
    /**
     * Whether the chip is an icon-only.
     * @default false
     */
    iconOnly?: boolean;

    /**
     * The content of the chip. Can be a string or ReactNode. Disallowed if 'iconOnly' is true.
     */
    children?: ReactNode;
};

export type ChipProps = ChipBaseProps & LayoutProps;

const defaultSize: DesignSystemChipSize = 'md';
const defaultColorScheme: DesignSystemColorScheme = 'accent1';
const defaultRadius: DesignSystemChipRadius = 'rounded';

/**
 * Chip component wrapping MUI Chip, providing styles based on the design system.
 */
export const Chip = forwardRef<HTMLDivElement, ChipProps>((props, ref) => {
    const mergedProps = useSlotContext(props, props.slot);
    const {
        children,
        size = defaultSize,
        colorScheme = defaultColorScheme,
        radius = defaultRadius,
        iconOnly = false,
        slot,
        sx,
        ...rest
    } = mergedProps;
    const theme = useTheme();
    const colors = theme.palette.ds;
    let hasLeadingItem = false;
    let hasTrailingItem = false;

    Children.forEach(children, (child) => {
        if (isValidElement<{ slot: keyof Slots }>(child)) {
            if (child.props.slot === 'leadingItem') {
                hasLeadingItem = true;
            }
            if (child.props.slot === 'trailingItem') {
                hasTrailingItem = true;
            }
        }
    });

    const { chipStyles, iconSize, typographyVariant } = useMemo(() => {
        const textColor = colors.fg[colorScheme].primary;
        const bgColor = colors.bg[colorScheme].tertiary;
        const borderColor =
            colorScheme === 'neutral'
                ? colors.border[colorScheme].primary
                : colors.border[colorScheme].tertiary;

        const sizeStyles = getSizeStyles(
            size,
            iconOnly,
            hasLeadingItem,
            hasTrailingItem,
        );
        const sizeDef = chipSizeValues[size];
        const iconSize = sizeDef.iconSize;
        const typographyVariant = sizeDef.typographyVariant;
        const radiusStyles = getRadiusStyles({
            componentRadiusValues: chipRadiusValues,
            radius,
            size,
            theme,
        });

        const chipStyles: SxProps<Theme> = {
            color: textColor,
            backgroundColor: bgColor,
            border: `1px solid ${borderColor}`,
            height: sizeStyles.height,
            ...(iconOnly
                ? {
                      width: sizeDef.height,
                      minWidth: sizeStyles.minWidth,
                  }
                : {
                      maxWidth: '100%',
                  }),
            paddingLeft: sizeStyles.paddingLeft,
            paddingRight: sizeStyles.paddingRight,
            paddingY: sizeStyles.paddingY,
            borderRadius: radiusStyles.borderRadius,
            display: 'inline-flex',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            verticalAlign: 'middle',
            fontWeight: 'semibold',
            gap: sizeDef.gap,
        };
        return {
            sizeStyles,
            chipStyles,
            iconSize,
            typographyVariant,
        };
    }, [
        colorScheme,
        colors,
        iconOnly,
        size,
        theme,
        radius,
        hasLeadingItem,
        hasTrailingItem,
    ]);

    const slots: Slots = useMemo(
        () => ({
            label: {
                color: 'inherit',
                component: 'span',
                variant: typographyVariant,
                fontWeight: 'inherit',
                sx: {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                },
            },
            leadingItem: {
                color: 'inherit',
                size: iconSize,
            },
            trailingItem: {
                color: 'inherit',
                size: iconSize,
            },
            icon: {
                color: 'inherit',
                size: iconSize,
            },
        }),
        [typographyVariant, iconSize],
    );

    return (
        <SlotProvider value={slots}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                ref={ref}
                sx={{ ...chipStyles, ...sx }}
                {...rest}
            >
                {typeof children === 'string' ? (
                    <Typography slot="label">{children}</Typography>
                ) : (
                    children
                )}
            </Stack>
        </SlotProvider>
    );
});
